import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Archive, ArchiveRestore, Check, Pencil, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FormError, PageHeader } from "@/components/common/ui-bits";
import { api, errMsg } from "@/lib/api";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

function CategoryItem({ cat, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(cat.name);
  const [error, setError] = useState("");
  const id = slug(cat.name);

  const patch = async (body) => {
    try {
      await api.patch(`/categories/${cat.id}`, body);
      setEditing(false); setError("");
      onChanged();
      toast.success(body.is_archived === true ? "Category archived" : body.is_archived === false ? "Category restored" : "Category renamed");
    } catch (e) { setError(errMsg(e)); }
  };

  return (
    <li className="px-4 py-3" data-testid={`category-item-${id}`}>
      <div className="flex items-center gap-2">
        {editing ? (
          <>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="h-9 rounded-lg" data-testid={`category-rename-input-${id}`} autoFocus />
            <Button size="icon" className="h-9 w-9 shrink-0" onClick={() => patch({ name })} data-testid={`category-rename-save-${id}`} aria-label="Save"><Check className="h-4 w-4" /></Button>
            <Button size="icon" variant="ghost" className="h-9 w-9 shrink-0" onClick={() => { setEditing(false); setName(cat.name); setError(""); }} aria-label="Cancel"><X className="h-4 w-4" /></Button>
          </>
        ) : (
          <>
            <div className="min-w-0 flex-1">
              <div className={`text-sm font-medium truncate ${cat.is_archived ? "text-muted-foreground line-through decoration-1" : ""}`}>{cat.name}</div>
              <div className="text-[11px] text-muted-foreground">
                {cat.transaction_count} {cat.transaction_count === 1 ? "entry" : "entries"}{cat.is_default ? " · default" : ""}{cat.is_archived ? " · archived" : ""}
              </div>
            </div>
            <Button size="sm" variant="ghost" className="h-8" onClick={() => setEditing(true)} data-testid={`category-rename-${id}`}><Pencil className="h-3.5 w-3.5 mr-1" />Rename</Button>
            {cat.is_archived ? (
              <Button size="sm" variant="outline" className="h-8" onClick={() => patch({ is_archived: false })} data-testid={`category-restore-${id}`}><ArchiveRestore className="h-3.5 w-3.5 mr-1" />Restore</Button>
            ) : (
              <Button size="sm" variant="outline" className="h-8" onClick={() => patch({ is_archived: true })} data-testid={`category-archive-${id}`}><Archive className="h-3.5 w-3.5 mr-1" />Archive</Button>
            )}
          </>
        )}
      </div>
      {error && <div className="mt-2"><FormError message={error} testId={`category-error-${id}`} /></div>}
    </li>
  );
}

function AddCategory({ onAdded }) {
  const [name, setName] = useState("");
  const [type, setType] = useState("expense");
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/categories", { name, type });
      toast.success("Category added");
      setName(""); setError("");
      onAdded();
    } catch (err) { setError(errMsg(err)); }
  };
  return (
    <form onSubmit={submit} className="surface p-4 sm:p-5 mb-6 fade-up" data-testid="add-category-form">
      <div className="flex flex-col sm:flex-row gap-3">
        <Input value={name} onChange={(e) => { setName(e.target.value); setError(""); }} placeholder="New category name, e.g. Insurance" className="h-11 rounded-xl flex-1" data-testid="add-category-name-input" />
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="h-11 rounded-xl sm:w-40" data-testid="add-category-type-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="expense" data-testid="add-category-type-expense">Expense</SelectItem>
            <SelectItem value="income" data-testid="add-category-type-income">Income</SelectItem>
          </SelectContent>
        </Select>
        <Button type="submit" className="h-11 rounded-xl" data-testid="add-category-submit-button"><Plus className="h-4 w-4 mr-1" />Add category</Button>
      </div>
      {error && <div className="mt-3"><FormError message={error} testId="add-category-error" /></div>}
    </form>
  );
}

export default function CategoriesPage() {
  const [cats, setCats] = useState([]);
  const load = useCallback(() => api.get("/categories").then((r) => setCats(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const column = (type, title, dot) => (
    <div className="surface overflow-hidden fade-up" data-testid={`categories-${type}-list`}>
      <div className="px-4 py-3 border-b border-border flex items-center gap-2">
        <span className={`h-2.5 w-2.5 rounded-full ${dot}`} /><h2 className="font-heading font-semibold">{title}</h2>
      </div>
      <ul className="divide-y divide-border">
        {cats.filter((c) => c.type === type).map((c) => <CategoryItem key={c.id} cat={c} onChanged={load} />)}
      </ul>
    </div>
  );

  return (
    <div className="max-w-5xl">
      <PageHeader eyebrow="Organise your entries" title="Categories" subtitle="Archived categories are hidden from Add Entry but stay on past entries and in totals." testId="categories-title" />
      <AddCategory onAdded={load} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {column("expense", "Expense categories", "bg-rose-500")}
        {column("income", "Income categories", "bg-emerald-500")}
      </div>
    </div>
  );
}
